import { IMiddlewareEvent } from 'mobx-state-tree';
import { Query } from 'mst-gql';
import { ClientError } from 'graphql-request';
import getAppEnv from '../utils/getAppEnv';
import { GraphQLErrorCodes } from '../../../constants';
import * as ScheduleNotifications from '../../../notifications/scheduling/schedule';
import { bulkAssignStopFailedNotification } from '../../../notifications/scheduling/assignStop';
import * as DuplicateRouteNotifications from '../../../notifications/routes/duplicateRoute';

const MSTGQL_ACTIONS = ['query', 'mutate'];

const isMstGqlAction = (call: IMiddlewareEvent) => (
  call.type === 'action' && MSTGQL_ACTIONS.indexOf(call.name) > -1
);

const SCHEDULING_ACTIONS = [
  GraphQLErrorCodes.OPTIMIZE_ORDERS_TO_MULTIPLE_TARGET_ROUTES_FAILED,
  GraphQLErrorCodes.OPTIMIZE_FAILED,
  GraphQLErrorCodes.OPTIMIZE_ROUTES_FAILED,
  GraphQLErrorCodes.SCHEDULE_STOPS_ON_DATE_FAILED,
];

const isSchedulingAction = (code: GraphQLErrorCodes) => (
  SCHEDULING_ACTIONS.indexOf(code) > -1
);

export default (call: IMiddlewareEvent, next: (actionCall: IMiddlewareEvent, callback?: (value: any) => any) => void) => {
  if (isMstGqlAction(call)) {
    return next(call, (value: any) => {
      if (value instanceof Query) {
        value.currentPromise().catch((error: ClientError | undefined) => {
          const responseErrors = error?.response?.errors;
          if (!responseErrors || responseErrors.length === 0) return;

          const usageLimitError = responseErrors.find((err) => err.extensions?.code === GraphQLErrorCodes.USAGE_LIMIT_EXCEEDED);
          const duplicateRouteError = responseErrors.find((err) => err.extensions?.code === GraphQLErrorCodes.DUPLICATE_ROUTE_FAILED);
          const schedulingError = responseErrors.find((err) => err.extensions?.code && isSchedulingAction(err.extensions.code as GraphQLErrorCodes));
          const workspaceId = call.tree.ui.selectedWorkspace?.id;

          if (usageLimitError) {
            return;
          }

          if (duplicateRouteError && workspaceId) {
            const errorMessage = duplicateRouteError.message;
            getAppEnv(call.tree).notification(DuplicateRouteNotifications.duplicateRouteFailureNotification(workspaceId, errorMessage));
            return;
          }

          if (schedulingError) {
            // backward compatibility to handle old error code
            if (schedulingError.extensions.code === GraphQLErrorCodes.OPTIMIZE_ORDERS_TO_MULTIPLE_TARGET_ROUTES_FAILED) {
              getAppEnv(call.tree).notification(bulkAssignStopFailedNotification());
            } else {
              // When the backend attaches the structured engine error (code + violations) to the
              // GraphQL error extensions, route it through the violation mapper so the user sees a
              // specific reason; otherwise fall back to the raw message.
              // Only route through the violation mapper when the structured engine error actually
              // carries violations; otherwise keep surfacing the raw backend message (a structured
              // error with a code but no violations would otherwise collapse to a generic string).
              const engineError = schedulingError.extensions?.error as { details?: { violations?: unknown[] } } | undefined;
              const output = engineError?.details?.violations?.length
                ? { details: { error: engineError } }
                : { error: schedulingError.message };
              getAppEnv(call.tree).notification(ScheduleNotifications.scheduleFailureNotification(workspaceId, output));
              // Also surface the reason on the persistent Unscheduled-panel banner (consistent with
              // the subscription handlers), not just the transient toast.
              call.tree.ui.setSchedulingError(ScheduleNotifications.buildOutputError(output));
            }
          }
        });
      }

      return value;
    });
  }

  return next(call);
};
